import { motion } from 'framer-motion'
import { Bot, CheckCircle } from 'lucide-react'

interface StrategyProgressProps {
  progress: string[]
  isVisible: boolean
}

export function StrategyProgress({ progress, isVisible }: StrategyProgressProps) {
  if (!isVisible || progress.length === 0) return null

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
    >
      <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full mx-4">
        <div className="text-center mb-6">
          <div className="flex items-center justify-center mb-4">
            <div className="h-12 w-12 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center mr-3">
              <Bot className="h-6 w-6" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900">Generating AI campaign setup</h3>
          </div>
          <p className="text-gray-600">AgenX is creating your comprehensive campaign setup...</p>
          <div className="mt-2">
            <div className="inline-flex items-center space-x-1">
              <div className="w-2 h-2 bg-primary-600 rounded-full animate-bounce"></div>
              <div className="w-2 h-2 bg-primary-600 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
              <div className="w-2 h-2 bg-primary-600 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          {progress.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className="flex items-center space-x-3"
            >
              <div className="flex-shrink-0">
                <CheckCircle className="h-5 w-5 text-green-500" />
              </div>
              <span className="text-sm text-gray-700">{step}</span>
            </motion.div>
          ))}
          {/* Show remaining steps as pending */}
          {Array.from({ length: 8 - progress.length }, (_, index) => (
            <motion.div
              key={`pending-${index}`}
              initial={{ opacity: 0.3 }}
              animate={{ opacity: 0.3 }}
              className="flex items-center space-x-3"
            >
              <div className="flex-shrink-0">
                <div className="h-5 w-5 rounded-full border-2 border-gray-300 bg-gray-100"></div>
              </div>
              <span className="text-sm text-gray-400">
                {progress.length + index < 8 ? 
                  ['Analyzing campaign requirements...', 'Generating AI-powered ad copies...', 'Creating creative variants...', 'Building media plan...', 'Optimizing audience strategy...', 'Selecting templates...', 'Calculating KPIs...', 'Finalizing strategy...'][progress.length + index] 
                  : 'Processing...'
                }
              </span>
            </motion.div>
          ))}
        </div>

        <div className="mt-6">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <motion.div
              className="bg-primary-600 h-2 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${Math.min((progress.length / 8) * 100, 95)}%` }}
              transition={{ duration: 0.5 }}
            />
          </div>
          <div className="text-center mt-2">
            <span className="text-xs text-gray-500">
              {progress.length === 8 ? '100% Complete' : `${Math.round((progress.length / 8) * 100)}% Complete`}
            </span>
          </div>
        </div>
      </div>
    </motion.div>
  )
}
